import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  BatchUpdatePricesRequestSchema,
  OfferPriceAllResponseSchema,
  OfferPriceListPageResponseSchema,
  OfferPriceListQuerySchema,
  OfferPricingTotalsSchema,
} from '../schemas/offer-pricing.js';
import { OfferIdSchema } from '../schemas/offer-unit.js';

export const offerPricing = {
  /**
   * GET /offers/:offerId/prices
   * Cursor-paginated list of per-unit prices joined with media-unit metadata.
   * Returns one row per unit in the offer (null pricing fields when unset),
   * as a `{ data, meta: { nextCursor, pageSize, totalCount } }` envelope.
   * Auth: effective view.
   */
  list: endpoint(
    'GET',
    '/offers/:offerId/prices',
    z.object({ offerId: OfferIdSchema }).merge(OfferPriceListQuerySchema),
    OfferPriceListPageResponseSchema,
  ),

  /**
   * GET /offers/:offerId/prices/all
   * Whole-offer affordances source: every unit's price row, unpaginated, used
   * only by the pricing tab's "apply discount to all" flow (needs the exact
   * row count and full unit-ID list regardless of the loaded page).
   * Auth: effective view.
   */
  listAll: endpoint(
    'GET',
    '/offers/:offerId/prices/all',
    z.object({ offerId: OfferIdSchema }),
    OfferPriceAllResponseSchema,
  ),

  /**
   * PUT /offers/:offerId/prices
   * Batch upserts price rows for the given unit IDs.
   * Rows not in the payload remain unchanged.
   * Auth: effective view+edit.
   */
  batchUpdate: endpoint(
    'PUT',
    '/offers/:offerId/prices',
    z.object({ offerId: OfferIdSchema }).merge(BatchUpdatePricesRequestSchema),
    z.void(),
  ),

  /**
   * GET /offers/:offerId/prices/totals
   * Returns server-computed pricing totals (subtotalNet, discount, KIK, grandTotal).
   * Auth: effective view.
   */
  totals: endpoint(
    'GET',
    '/offers/:offerId/prices/totals',
    z.object({ offerId: OfferIdSchema }),
    OfferPricingTotalsSchema,
  ),
};
